import { MessageSquare,Paperclip, Send, Trash2, X } from 'lucide-react';
import React, { useEffect, useMemo,useRef, useState } from 'react';

import { assistenteService, detectObraForChat } from '@features/ia/services/assistenteService';

interface ChatMessage {
  id: string;
  text: string;
  sender: 'user' | 'assistant';
  timestamp: Date;
  image?: string;
}

// Ações rápidas → disparam intenções que mapeiam para RPCs
const QUICK_ACTIONS = [
  { label: 'Termômetro', text: 'mostrar termômetro da obra' },
  { label: 'Prazos (7d)', text: 'quais prazos vencem esta semana' },
  { label: 'Subetapas atrasadas', text: 'listar subetapas com atraso' },
  { label: 'Serviços em risco', text: 'quais serviços estão em risco' },
  { label: 'Compras em risco', text: 'requisicoes de compras em risco' },
  { label: 'Threads recentes', text: 'threads quentes desta obra' },
  { label: 'Top pendências', text: 'top pendências críticas' },
  { label: 'Ocorrências (14d)', text: 'ocorrencias dos diarios nos ultimos 14 dias' },
  { label: 'Clima', text: 'clima e produtividade' },
  { label: 'Efetivo & Equip.', text: 'efetivo e equipamentos últimos 14 dias' },
  { label: 'Checklists', text: 'checklists recentes' },
  { label: 'Parados', text: 'itens parados' },
];

const AssistenteChat: React.FC = () => {
  // ✅ Estados locais seguros
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [detectedObra, setDetectedObra] = useState<{ obraId?: string; obraNome?: string; source?: string; confidence?: number }>({});
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ✅ Detectar mobile de forma segura
  const isMobile = useMemo(() => {
    try {
      return window.innerWidth < 768;
    } catch (error) {
      return false;
    }
  }, []);

  // ✅ Detectar obra atual da URL
  const obraId = useMemo(() => {
    try {
      const path = window.location.pathname;
      const match = path.match(/\/obras\/([^\/]+)/);
      return match ? match[1] : null;
    } catch (error) {
      console.warn('Erro ao detectar obra atual:', error);
      return null;
    }
  }, []);

  // ✅ Escutar evento de abertura do chat e detectar obra
  useEffect(() => {
    try {
      const handleOpenChat = async () => {
        setIsOpen(true);
        
        // Adicionar mensagem de boas-vindas apenas se for a primeira vez
        if (messages.length === 0) {
          // Detectar obra ao abrir o chat
          let obraDetectada = detectedObra;
          try {
            const res = await detectObraForChat(undefined, undefined);
            setDetectedObra(res);
            obraDetectada = res;
          } catch (error) {
            console.warn('Erro ao detectar obra:', error);
          }
          
          const welcomeMessage: ChatMessage = {
            id: 'welcome-' + Date.now(),
            text: generateWelcomeMessage(obraDetectada),
            sender: 'assistant',
            timestamp: new Date()
          };
          setMessages([welcomeMessage]);
        }
      };

      window.addEventListener('acor-open-chat', handleOpenChat);
      return () => window.removeEventListener('acor-open-chat', handleOpenChat);
    } catch (error) {
      console.warn('Erro ao configurar listener do chat:', error);
    }
  }, [messages.length, detectedObra]);

  // ✅ Scroll automático para última mensagem
  useEffect(() => {
    try {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    } catch (error) {
      console.warn('Erro no scroll automático:', error);
    }
  }, [messages]);

  // ✅ Função para enviar mensagem - INTEGRAÇÃO REAL COM CHATGPT
  const handleSendMessage = async () => {
    if (!inputMessage.trim() && !selectedImage) return;

    try {
      // Re-detecta a obra com base na pergunta
      const currentInput = inputMessage.trim();
      const res = await detectObraForChat(currentInput, undefined);
      if (res?.obraId && (res.obraId !== detectedObra?.obraId || res.obraNome !== detectedObra?.obraNome)) {
        setDetectedObra(res);
      }
      
      // Criar mensagem do usuário
      const userMessage: ChatMessage = {
        id: 'user-' + Date.now(),
        text: currentInput,
        sender: 'user',
        timestamp: new Date(),
        image: selectedImage ? URL.createObjectURL(selectedImage) : undefined
      };

      setMessages(prev => [...prev, userMessage]);
      const currentImage = selectedImage;
      setInputMessage('');
      setSelectedImage(null);
      setIsLoading(true);

      console.log('🤖 Enviando mensagem para ChatGPT:', {
        message: currentInput.substring(0, 50) + '...',
        hasImage: !!currentImage,
        resolvedObraId: res.obraId,
        resolvedFrom: res.source,
        confidence: res.confidence
      });

      let response: string;

      // ✅ INTEGRAÇÃO REAL COM ASSISTENTE SERVICE
      if (currentImage) {
        // Análise de imagem com ChatGPT
        response = await assistenteService.analyzeImage(
          currentImage,
          currentInput || 'Analise esta imagem da obra seguindo padrões ACOR',
          res.obraId || undefined
        );
      } else {
        // Processamento de mensagem de texto com ChatGPT
        response = await assistenteService.processMessage(
          currentInput,
          { obraId: res.obraId }
        );
      }

      // Criar mensagem de resposta do ChatGPT
      const assistantMessage: ChatMessage = {
        id: 'assistant-' + Date.now(),
        text: response,
        sender: 'assistant',
        timestamp: new Date()
      };

      setMessages(prev => [...prev, assistantMessage]);
      console.log('✅ Resposta do ChatGPT recebida e exibida');

    } catch (error) {
      console.error('❌ Erro ao processar mensagem com ChatGPT:', error);
      
      const errorMessage: ChatMessage = {
        id: 'error-' + Date.now(),
        text: getErrorMessage(),
        sender: 'assistant',
        timestamp: new Date()
      };
      
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  // ✅ Função para selecionar arquivo
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const file = event.target.files?.[0];
      if (file && file.type.startsWith('image/')) {
        // Verificar tamanho (máximo 10MB)
        if (file.size > 10 * 1024 * 1024) {
          alert('Imagem muito grande. Por favor, envie arquivos menores que 10MB.');
          return;
        }
        setSelectedImage(file);
      } else {
        alert('Por favor, selecione apenas arquivos de imagem.');
      }
    } catch (error) {
      console.error('Erro ao selecionar arquivo:', error);
    }
  };

  // ✅ Função para limpar chat
  const handleClearChat = () => {
    try {
      setMessages([]);
      setInputMessage('');
      setSelectedImage(null);
      // Limpar cache do assistente para a obra atual
      if (obraId) {
        assistenteService.clearContextCache(obraId);
      }
      console.log('🗑️ Chat limpo - cache do contexto também limpo');
    } catch (error) {
      console.error('Erro ao limpar chat:', error);
    }
  };

  // ✅ Fechar chat
  const handleCloseChat = () => {
    try {
      setIsOpen(false);
    } catch (error) {
      console.error('Erro ao fechar chat:', error);
    }
  };

  // ✅ Enter para enviar mensagem
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // ✅ Se chat não está aberto, não renderizar
  if (!isOpen) return null;

  return (
    <div className={`fixed inset-0 bg-black bg-opacity-50 z-50 ${isMobile ? 'flex items-center justify-center' : 'flex items-end justify-end'}`}>
      <div 
        className={`bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-xl
          ${isMobile 
            ? 'w-full h-full mx-4 my-8 rounded-2xl' 
            : 'w-96 h-[600px] m-4 mb-20 rounded-lg'
          }`}
        style={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          maxHeight: isMobile ? 'calc(100vh - 64px)' : '800px',
        }}
      >
        {/* ✅ Header */}
        <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center">
              <MessageSquare className="w-4 h-4 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <h3 className="font-ageo-bold text-foreground dark:text-gray-100">Assistente ACOR</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {isLoading ? 'Pensando com IA...' : 'ChatGPT Online'}
                {obraId && ' • Obra atual'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleClearChat}
              className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              title="Limpar conversa"
            >
              <Trash2 className="w-4 h-4" />
            </button>
            <button
              onClick={handleCloseChat}
              className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              title="Fechar chat"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* ✅ Ações rápidas */}
        <div className="px-4 pt-3">
          <div className="flex flex-wrap gap-2">
            {QUICK_ACTIONS.map((a) => (
              <button
                key={a.label}
                onClick={() => { setInputMessage(a.text); handleSendMessage(); }}
                className="text-xs px-3 py-1 rounded-full border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700"
                title={a.text}
              >
                {a.label}
              </button>
            ))}
          </div>
        </div>

        {/* ✅ Mensagens */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.map((message) => (
            <div
              key={message.id}
              className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[85%] p-3 rounded-lg ${
                  message.sender === 'user'
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100'
                }`}
              >
                {message.image && (
                  <img
                    src={message.image}
                    alt="Anexo"
                    className="w-full max-w-48 rounded mb-2"
                  />
                )}
                <MessageContent message={message} />
                <div className={`text-xs mt-1 ${
                  message.sender === 'user' ? 'text-blue-100' : 'text-gray-500 dark:text-gray-400'
                }`}>
                  {message.timestamp.toLocaleTimeString('pt-BR', { 
                    hour: '2-digit', 
                    minute: '2-digit' 
                  })}
                </div>
              </div>
            </div>
          ))}
          
          {isLoading && (
            <div className="flex justify-start">
              <div className="bg-gray-100 dark:bg-gray-700 p-3 rounded-lg">
                <div className="flex items-center space-x-2">
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                    <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                  </div>
                  <span className="text-xs text-gray-500 dark:text-gray-400">ChatGPT analisando...</span>
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        {/* ✅ Preview da imagem selecionada */}
        {selectedImage && (
          <div className="p-4 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-2 p-2 bg-gray-50 dark:bg-gray-700 rounded">
              <img
                src={URL.createObjectURL(selectedImage)}
                alt="Preview"
                className="w-12 h-12 object-cover rounded"
              />
              <span className="text-sm text-gray-600 dark:text-gray-300 flex-1">{selectedImage.name}</span>
              <button
                onClick={() => setSelectedImage(null)}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* ✅ Input */}
        <div className="p-4 border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <button
              onClick={() => fileInputRef.current?.click()}
              className={`text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200
                         ${isMobile ? 'p-3' : 'p-2'}`}
              title="Anexar imagem para análise"
            >
              <Paperclip className={`${isMobile ? 'w-5 h-5' : 'w-4 h-4'}`} />
            </button>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              accept="image/*"
              className="hidden"
            />
            <input
              type="text"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Pergunte sobre obra, análise técnica, orçamentos..."
              className={`flex-1 border border-gray-300 dark:border-gray-600 rounded-lg 
                         bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100
                         focus:outline-none focus:ring-2 focus:ring-blue-500
                         ${isMobile ? 'px-4 py-4 text-base' : 'px-3 py-2'}`}
              disabled={isLoading}
            />
            <button
              onClick={handleSendMessage}
              disabled={(!inputMessage.trim() && !selectedImage) || isLoading}
              className={`bg-blue-500 text-white rounded-lg hover:bg-blue-600 
                         disabled:opacity-50 disabled:cursor-not-allowed
                         ${isMobile ? 'p-3' : 'p-2'}`}
              title="Enviar para ChatGPT"
            >
              <Send className={`${isMobile ? 'w-5 h-5' : 'w-4 h-4'}`} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * ✅ Componente para renderizar conteúdo das mensagens com funcionalidade de expansão
 */
const MessageContent: React.FC<{ message: ChatMessage }> = ({ message }) => {
  const [expanded, setExpanded] = useState(false);
  const tooLong = (message.text?.length || 0) > 4000;
  
  return (
    <div className="text-sm">
      <div className="whitespace-pre-wrap">{message.text}</div>
      {tooLong && !expanded && (
        <div className="mt-2">
          <button
            onClick={() => setExpanded(true)}
            className="text-sm px-3 py-1 rounded bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600"
          >
            Mostrar mais
          </button>
        </div>
      )}
    </div>
  );
};

/**
 * ✅ Gera mensagem de boas-vindas com recursos reais do ChatGPT
 */
function generateWelcomeMessage(detectedObra: { obraId?: string; obraNome?: string; source?: string; confidence?: number }): string {
  const nome = detectedObra?.obraNome?.trim();
  const id = detectedObra?.obraId;
  const rotuloObra = nome ? nome : (id ? `#${id.slice(0, 8)}…` : 'Não detectada');
  
  if (detectedObra?.obraId) {
    return `🤖 **Assistente Virtual ACOR com ChatGPT**

📍 **Obra Detectada**: ${rotuloObra}

🆕 **AGORA COM INTELIGÊNCIA ARTIFICIAL REAL!**

Estou integrado com ChatGPT e posso analisar dados reais da sua obra extraídos dos arquivos!

**🔧 CAPACIDADES AVANÇADAS:**
✅ **Análise técnica especializada** com IA real
✅ **Orçamentos detalhados** com valores específicos dos arquivos
✅ **Cronogramas precisos** com datas extraídas dos documentos
✅ **Análise de fotos** com IA visual avançada
✅ **Orientações técnicas** personalizadas para ACOR

**💡 EXEMPLOS REAIS:**
• "Qual o valor total do forro nesta obra?"
• "Como fazer contrapiso nivelado seguindo normas?"
• "Analise o cronograma e me diga os próximos prazos"
• "Quais os custos por categoria desta obra?"
• Envie fotos para análise técnica automática

**Valores ACOR**: 🏃‍♂️ Agilidade | 🤝 Confiança | 📋 Organização | 🎯 Resultado

Faça sua pergunta técnica ou envie uma foto!`;
  }

  return `🤖 **Assistente Virtual ACOR com ChatGPT**

🆕 **AGORA COM INTELIGÊNCIA ARTIFICIAL REAL!**

Sou seu assistente especializado em construção civil, integrado com ChatGPT para análises avançadas.

**🔧 ESPECIALIDADES COM IA:**
• Orientações técnicas de execução com precisão
• Análise de custos e processos construtivos
• Cronogramas e planejamento de obras
• Análise automática de fotos da obra
• Normas técnicas e procedimentos de segurança
• Padrões de qualidade ACOR de alto padrão

**💡 EXEMPLOS DE CONSULTAS:**
• "Como fazer contrapiso nivelado seguindo normas?"
• "Qual a sequência ideal para instalação elétrica?"
• "Padrões ACOR para acabamento em clínicas"
• "Analise esta foto da obra" (envie imagem)
• "Normas de segurança para obras hospitalares"

💡 **Dica**: Acesse uma obra específica para análises ainda mais detalhadas com dados reais dos arquivos!

**Valores ACOR**: Agilidade, Confiança, Organização e Resultado

Como posso ajudar você hoje?`;
}

/**
 * ✅ Mensagem de erro amigável para problemas com ChatGPT
 */
function getErrorMessage(): string {
  return `🤖 **Assistente ACOR**

Ocorreu um problema temporário com a IA. Possíveis causas:

• **Conectividade**: Verifique sua conexão com internet
• **API**: ChatGPT pode estar temporariamente indisponível
• **Configuração**: Verifique se API está configurada

**🔄 O que fazer:**
• Tente novamente em alguns instantes
• Use as funcionalidades normais do sistema
• Consulte documentos da obra diretamente
• Entre em contato com suporte se persistir

**📋 ACESSO DIRETO:**
Lembre-se: você tem acesso completo a todas as informações através do sistema ACOR!

*Os valores de Agilidade, Confiança, Organização e Resultado continuam guiando nosso trabalho.*`;
}

export default AssistenteChat;